/**
 * VMS (Video Management System) TypeScript interfaces
 * Based on the Edge Node API Swagger specification
 */

import type { EdgeDevice } from "./api";

/**
 * VMS Device Status enumeration
 * As defined in the API schema (components.schemas.VmsDeviceStatus)
 */
export enum VmsDeviceStatus {
    UNDEFINED = "undefined",
    UNKNOWN = "unknown",
    ONLINE = "online",
    OFFLINE = "offline",
    UNAUTHORIZED = "unauthorized",
    INCOMPATIBLE = "incompatible",
    RECORDING = "recording",
    ERROR = "error",
    UNCONFIGURED = "unconfigured",
}

/**
 * VMS (Video Management System) interface
 * Based on CreateVmsDto and UpdateVmsDto schemas
 */
export interface VMS {
    id?: number;
    vendor: string; // Manufacturer name of the VMS (e.g., "Hanwha")
    name: string; // Name of the VMS (e.g., "Aqsacom France")
    model: string; // Model name of the VMS (e.g., "Wave")
    status?: VmsDeviceStatus; // Status of the VMS
    version: string; // Software Version of the VMS (e.g., "1.0.0")
    fqdn: string; // FQDN to the VMS API (e.g., "https://vms.example.com:7000")
    username: string; // Username for the VMS API
    password?: string; // Password for the VMS API (write-only)
    info?: Record<string, any>; // Vendor/VMS specific information in JSON format
}

/**
 * VMS Device interface (Camera)
 * Based on CreateVmsDeviceDto and UpdateVmsDeviceDto schemas
 */
export interface VMSDevice {
    id?: number;
    type: string; // Device type (e.g., "Camera")
    vendor: string; // Device vendor (e.g., "Axis")
    model: string; // Device model (e.g., "P5635-E")
    name: string; // Human-readable device name (e.g., "Lobby Cam 01")
    status: VmsDeviceStatus; // Operational status of the device
    version: string; // Firmware/software version (e.g., "10.2.1")
    vmsId: number; // Related VMS ID (foreign key)
    vmsCameraId: string; // VMS camera identifier (e.g., "dc85c34a-3631-7151-703d-b678e1935bed")
    lat: number; // Latitude (WGS84, -90 to 90)
    long: number; // Longitude (WGS84, -180 to 180)
    spaceId?: number; // Optional space ID
    levelId?: number; // Optional level ID
    buildingId?: number; // Optional building ID
    venueId?: number; // Optional venue ID
    info?: Record<string, any>; // JSON metadata as retrieved from the VMS
}

/**
 * Stream State interface for tracking active streams and recovery attempts
 */
export interface StreamState {
    deviceId: string;
    vmsId: number;
    vmsCameraId: string;
    isActive: boolean;
    isRecovering: boolean;
    recoveryAttempts: number;
    lastError?: string;
    lastProgressTimestamp: number;
    streamUrl?: string;
    positionMs?: number; // For recorded playback, 0 or undefined for live
    recordedBaseMs?: number; // Base timestamp for recorded content
    lastFloorMs?: number; // Monotonic progress tracking
}

/**
 * Media Token interface for token management with expiration
 */
export interface MediaToken {
    token: string;
    expiresAt?: number; // Unix timestamp when token expires
    fetchedAt: number; // Unix timestamp when token was fetched
}

/**
 * VMS API Response interfaces
 */
export interface VMSListResponse {
    data: VMS[];
    total?: number;
}

export interface VMSDeviceListResponse {
    data: VMSDevice[];
    total?: number;
}

export interface VMSDeviceResponse {
    data: VMSDevice;
}

/**
 * Stream and Image URL parameters
 */
export interface StreamParams {
    vmsId: number;
    deviceId: string;
    token: string;
    format: string; // "mp4" for video streaming
    params: string; // "none" for live, "positionMs=${timestamp}" for recorded
}

export interface ImageParams {
    vmsId: number;
    deviceId: string;
    token: string;
    params: string; // Timestamp parameters for thumbnail navigation
}

/**
 * Hybrid Device interface
 * Combines device data from /devices endpoint with VMS streaming capabilities
 */
export interface HybridDevice extends EdgeDevice {
    // VMS streaming capabilities
    vms: {
        available: boolean;
        vmsId?: number;
        vmsCameraId?: string;
        vendor?: string;
        model?: string;
        status?: VmsDeviceStatus;
        version?: string;
        info?: Record<string, any>;
        reason?: string; // When not available, reason why
        /**
         * The VMS device list has not arrived yet, so this camera has not been
         * looked up - unavailable for now, but nothing is known to be wrong.
         */
        pending?: boolean;
    };
}

/**
 * Raw camera record from `cvi/vms/closestdevices`.
 *
 * snake_case, unlike every other device payload in the app, and `type` is the
 * VMS model class ("MultisensorCamera") rather than an EdgeDevice type.
 * The three `vmsCameraId` spellings below — the plain one, `"vmsCameraId "` with
 * a trailing space, and the original `vmsCameraIdc` typo — are kept as contract
 * documentation of what the endpoint has shipped, not as a normalization input:
 * nothing reads them. The streaming GUID comes from the VMS cache join in
 * `matchDevicesWithVMS`, matched on `dbId`.
 */
export interface ClosestCamera {
    /** Numeric VMS device id — equals EdgeDevice.dbId */
    id: number;
    name: string;
    /** Metres from the incident marker */
    distance: number;
    lat: number;
    long: number;
    type: string;
    model?: string;
    vendor?: string;
    status?: string;
    version?: string;
    vmsId?: number;
    vmsCameraId?: string;
    /** Interim spelling on some builds */
    "vmsCameraId "?: string;
    /** Original typo, still possible on lagging environments */
    vmsCameraIdc?: string;
    level_id: number | null;
    space_id: number | null;
    venue_id: number | null;
    building_id: number | null;
    created_at: string;
    updated_at: string;
}

export interface ClosestMarkerGroup {
    marker_id: number;
    marker_name: string | null;
    cameras: ClosestCamera[];
}

export interface ClosestIncidentGroup {
    incident_id: number;
    incident_name: string | null;
    markers: ClosestMarkerGroup[];
}

/**
 * A camera in the no-incident branch.
 *
 * Deliberately NOT `ClosestCamera`. When a venue has no active incident the
 * endpoint returns a different record: already camelCase, carrying `rank`, and
 * with no `distance` because there is no marker to measure from.
 */
export interface ClosestDefaultCamera {
    /** Numeric VMS device id — equals EdgeDevice.dbId */
    id: number;
    name: string;
    type: string;
    /** Operator-assigned order. Null until someone ranks the camera. */
    rank: number | null;
    lat: number;
    long: number;
    model?: string;
    vendor?: string;
    status?: string;
    version?: string;
    vmsId?: number;
    vmsCameraId?: string;
    levelId: number | null;
    spaceId: number | null;
    venueId: number | null;
    buildingId: number | null;
    createdAt: string;
    updatedAt: string;
}

/**
 * The no-incident branch returns a single group holding cameras directly, with
 * no `markers` array and none of the incident fields.
 */
export interface ClosestDefaultGroup {
    cameras: ClosestDefaultCamera[];
}

/** One `data` element is either an incident group or the ranked default list. */
export type ClosestGroup = ClosestIncidentGroup | ClosestDefaultGroup;

/** Paginated envelope. `data` is null past the last page, and also when a
 *  venue/level combination has nothing to return. */
export interface ClosestDevicesResponse {
    page: number;
    /** Spelled `pagesize` on the incident branch and `pageSize` on the other. */
    pagesize?: number;
    pageSize?: number;
    total: number;
    data: ClosestGroup[] | null;
}

export interface ClosestDevicesParams {
    page?: number;
    pageSize?: number;
    venueId?: number | null;
    levelId?: number | null;
    incidentId?: number | null;
    /** Camera name search. */
    pattern?: string | null;
}

/**
 * A ranked camera ready for the widget.
 *
 * `distance` is present only for incident-ranked cameras. The no-incident list
 * is ordered by operator-assigned rank, where there is nothing to measure from,
 * so the widget suppresses the badge rather than printing a fabricated zero.
 */
export interface RankedCamera extends HybridDevice {
    distance?: number;
}

/**
 * VMS Store State interface
 */
export interface VMSStoreState {
    // Configuration
    availableVMS: VMS[];
    currentVMSId: number | null;

    // Authentication
    mediaToken: MediaToken | null;

    // Stream State
    activeStreams: Map<string, StreamState>;
    streamRecoveryAttempts: Map<string, number>;

    // Cache
    deviceListCache: Map<string, VMSDevice[]>;
    lastFetchTimestamp: Map<string, number>;
    thumbnailCache: Map<string, { url: string; timestamp: number }>;

    // Loading states
    loading: boolean;
    error: string | null;
}
